import { prettyPrint } from '@base2/pretty-print-object';
import { Spacing } from '@persona-id/design-tokens';
import { CopyIcon } from '@persona-id/design-tokens/icons';
import nightOwlLight from 'prism-react-renderer/themes/nightOwlLight';
import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import copyToClipboard from 'dashboard/lib/utils/copyToClipboard';
import { LogSeverity } from 'dashboard/models/workflow-step';
import { Z_INDICES } from 'dashboard/styles/variables';
import Tooltip from 'design-system/components/Tooltip';

const Wrapper = styled.div`
  font-family: ${({ theme }) => theme.text.fontFamily.mono};
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  line-height: 1.5;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  border: 1px solid ${({ theme }) => theme.color.border.input};
  width: 100%;
  background: ${nightOwlLight.plain.backgroundColor};
  padding: ${Spacing[2]};
  overflow: scroll;
`;

const CopyButton = styled.div`
  height: 16px;
  flex: 0 0 16px;
  background: #f4f4f6;
  visibility: hidden;
  cursor: pointer;
`;

const Row = styled.pre<{ severity: LogSeverity }>`
  display: flex;
  width: 100%;
  padding: ${Spacing[1]};
  color: ${({ severity, theme }) =>
    ({
      INFO: theme.color.text.primary,
      ERROR: theme.color.variants.danger.default,
      WARN: theme.color.variants.caution.default,
    })[severity]};

  &:hover {
    background: #f4f4f6;
    border-radius: ${({ theme }) => theme.element.borderRadius};

    & > ${CopyButton} {
      visibility: visible;
    }
  }
`;

const Logs = styled.div`
  max-width: 100%;
  flex: 1 1 auto;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const SingleLog = styled.span`
  display: inline-block;
  & + & {
    margin-left: ${Spacing[2]};
  }
`;

interface ConsoleOutputProps {
  logs: [LogSeverity, unknown[]][] | unknown[][];
}

const styleSingleLog = (log: unknown) =>
  typeof log === 'string'
    ? log
    : prettyPrint(log, {
        indent: '  ',
        inlineCharacterLimit: 60,
      });

const ConsoleOutput: FC<ConsoleOutputProps> = ({ logs }) => {
  const normalizedLogs: [LogSeverity, string[]][] = useMemo(() => {
    if (
      logs.every(
        (row) =>
          ['INFO', 'WARN', 'ERROR'].some((severity) => severity === row[0]) &&
          Array.isArray(row[1]),
      )
    ) {
      return (logs as [LogSeverity, unknown[]][]).map((row) => [
        row[0],
        row[1].map(styleSingleLog),
      ]);
    } else {
      return logs.map((row) => ['INFO', row.map(styleSingleLog)]);
    }
  }, [logs]);

  const [openedTooltip, setOpenedTooltip] = useState<number | `copied-${number}` | null>(null);
  const copyLog = async () => {
    if (typeof openedTooltip !== 'number') return;
    if (await copyToClipboard(normalizedLogs[openedTooltip][1].join(' '))) {
      setOpenedTooltip(`copied-${openedTooltip}`);
      setTimeout(() => {
        setOpenedTooltip(null);
      }, 1200);
    }
  };

  return (
    <Wrapper>
      {normalizedLogs.map(([severity, logsOnRow], i) => (
        <Row key={i} severity={severity}>
          <Logs>
            {logsOnRow.map((log, j) => (
              <SingleLog key={j}>{log}</SingleLog>
            ))}
          </Logs>
          <Tooltip
            open={openedTooltip != null}
            hideArrow
            side='left'
            zIndex={Z_INDICES.modalDropdown}
            trigger={
              <CopyButton
                aria-label={`Copy row #${i + 1}`}
                id={`console-output__copy-row-${i}`}
                title='Copy'
                onClick={copyLog}
                onMouseOver={() => setOpenedTooltip(i)}
                onMouseLeave={() => setOpenedTooltip(null)}
              >
                <CopyIcon size={16} />
              </CopyButton>
            }
          >
            {openedTooltip === `copied-${i}` ? 'Copied' : 'Copy'}
          </Tooltip>
        </Row>
      ))}
    </Wrapper>
  );
};

export default ConsoleOutput;
